import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Rocket, ChevronRight, ArrowUpRight, Code2, PenTool, Wifi, Database, MonitorPlay, Box, Clock, MapPin } from 'lucide-react';
import { Sec, SH, formatTime, formatDate } from '../components/Shared';
import Hero3DGraphic from '../components/Hero3DGraphic';
import { POSTER_THEMES, FEATURED_PROJECTS } from '../data/websiteData';

const HomePage = ({ events = [] }) => {
  const navigate = useNavigate();
  return (
    <div className="af">
      {/* HERO SECTION */}
      <section style={{
        padding: '180px 0 120px',
        background: 'var(--grad-hero)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div className="blob" style={{ position: 'absolute', top: -100, right: -100, width: 600, height: 600, background: 'rgba(59,130,246,.04)', filter: 'blur(80px)', zIndex: 0 }} />
        <div className="blob" style={{ position: 'absolute', bottom: -150, left: -200, width: 500, height: 500, background: 'rgba(139,92,246,.04)', filter: 'blur(80px)', zIndex: 0 }} />

        <div style={{ maxWidth: 1160, margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 40, flexWrap: 'wrap' }}>
          <div style={{ maxWidth: 640 }}>
            <div className="eyebrow au d1">Build. Innovate. Lead with Technology.</div>
            <h1 className="au d2" style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(44px, 8vw, 92px)',
              fontWeight: 800,
              color: 'var(--on-surface)',
              lineHeight: 0.9,
              letterSpacing: '-0.05em',
              marginBottom: 40
            }}>
              Ideal College<br /> <span className="gtext">IT Club</span><br />for Builders
            </h1>
            <p className="au d3" style={{ fontSize: 'clamp(18px, 2.2vw, 21px)', color: 'var(--on-surface-variant)', lineHeight: 1.5, marginBottom: 56, fontWeight: 500, maxWidth: 640 }}>
              We empower the next generation of tech leaders through hands-on learning, real-world projects and a collaborative innovation culture.
            </p>
            <div className="au d3" style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
              <Link to="/membership" className="btn btn-primary btn-lg">
                <Rocket size={18} /> Learn With Us
              </Link>
              <Link to="/knowledge-hub" className="btn btn-outline btn-lg">
                Explore Projects
              </Link>
            </div>

            <div className="au d3" style={{ marginTop: 100, display: 'flex', gap: 64 }}>
              {[['100+', 'Learners'], ['15+', 'Tech Events'], ['6+', 'Learning Tracks']].map(([v, l], i) => (
                <div key={i}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 800, color: 'var(--on-surface)', marginBottom: 4 }}>{v}</div>
                  <div style={{ fontSize: 10, fontWeight: 800, color: 'var(--slate-400)', textTransform: 'uppercase', letterSpacing: '.2em' }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="hide-m au d3" style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 320 }}>
            <Hero3DGraphic />
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <Sec py={140}>
        <div style={{ textAlign: 'center', marginBottom: 64 }}>
          <div className="eyebrow au" style={{ justifyContent: 'center' }}>&mdash; WHAT WE DO</div>
          <h2 className="au d1" style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(28px, 4vw, 36px)', fontWeight: 800, color: 'var(--on-surface)', marginBottom: 16 }}>Explore Technology Tracks</h2>
          <p className="au d2" style={{ fontSize: 16, color: 'var(--on-surface-variant)', maxWidth: 600, margin: '0 auto', lineHeight: 1.6 }}>Explore specialized tracks designed to help you master modern technology and build real-world skills.</p>
        </div>

        <div className="g3">
          {[
            { icon: <Code2 size={24} />, title: 'Web Development', sub: 'Learn how to build modern websites and web apps from scratch.', tags: ['HTML', 'CSS', 'JavaScript'], theme: { c: 'var(--primary)', bg: 'rgba(59,130,246,.08)' } },
            { icon: <PenTool size={24} />, title: 'UI/UX Design', sub: 'Explore design thinking and create user-friendly digital experiences.', tags: ['Figma', 'Stitch'], theme: { c: '#8B5CF6', bg: 'rgba(139,92,246,.08)' } },
            { icon: <Wifi size={24} />, title: 'Networking', sub: 'Learn how the internet works and explore system security basics.', tags: ['Cisco', 'Linux', 'Security'], theme: { c: '#10B981', bg: 'rgba(16,185,129,.08)' } },
            { icon: <Database size={24} />, title: 'Data & AI', sub: 'Explore data, analytics and the basics of artificial intelligence.', tags: ['Python', 'SQL', 'ML'], theme: { c: '#F59E0B', bg: 'rgba(245,158,11,.08)' } },
            { icon: <MonitorPlay size={24} />, title: 'Graphics & Media', sub: 'Learn to create visuals, branding and digital content.', tags: ['Photoshop', 'Illustrator', 'Canva'], theme: { c: '#8B5CF6', bg: 'rgba(139,92,246,.08)' } },
            { icon: <Box size={24} />, title: 'Tech Events', sub: 'Explore competitions, workshops and fun tech activities.', tags: ['Workshops', 'Hackathons', 'Tech Talks'], theme: { c: 'var(--primary)', bg: 'rgba(59,130,246,.08)' } }
          ].map((item, i) => (
            <div key={i} className={`card au d${i + 1}`} style={{ padding: 40, background: '#fff', border: '1px solid var(--border)' }}>
              <div style={{ marginBottom: 24, width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, color: item.theme.c, background: item.theme.bg }}>
                {item.icon}
              </div>
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 16 }}>{item.title}</h3>
              <p style={{ fontSize: 14, color: 'var(--on-surface-variant)', lineHeight: 1.6, marginBottom: 24, minHeight: 66 }}>{item.sub}</p>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {item.tags.map(t => (
                  <span key={t} style={{ fontSize: 11, fontWeight: 600, color: item.theme.c, background: item.theme.bg, padding: '4px 12px', borderRadius: 100 }}>{t}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Sec>

      {/* FEATURED PROJECTS (NEW) */}
      <Sec bg="var(--surface-container-low)" py={140}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 80 }}>
          <SH eyebrow="Projects" title="Explore Student Projects" sub="See what our members are learning and building." center={false} />
          <Link to="/blog" className="btn btn-ghost hide-m" style={{ marginBottom: 80 }}>
            View All Projects <ArrowUpRight size={16} />
          </Link>
        </div>
        <div className="g3">
          {FEATURED_PROJECTS.map((proj, i) => (
            <div key={proj.id} className={`card au d${i + 1}`} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ height: 200, overflow: 'hidden' }}>
                <img src={proj.img} alt={proj.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <div style={{ padding: 24, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--on-surface)', marginBottom: 12 }}>{proj.title}</h3>
                <p style={{ fontSize: 14, color: 'var(--on-surface-variant)', lineHeight: 1.6, marginBottom: 24, flex: 1 }}>{proj.desc}</p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {proj.tags.map(t => (
                    <span key={t} style={{ fontSize: 11, fontWeight: 600, color: 'var(--primary)', background: 'rgba(59,130,246,.08)', padding: '4px 12px', borderRadius: 100 }}>{t}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Sec>

      {/* RECENT EVENTS */}
      <Sec py={140}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 80 }}>
          <SH eyebrow="Timeline logs" title="Upcoming Events" sub="Don't miss our next technical sprints and workshops." center={false} />
          <Link to="/events" className="btn btn-ghost hide-m" style={{ marginBottom: 80 }}>
            Full Schedule <ArrowUpRight size={16} />
          </Link>
        </div>
        <div className="g3">
          {events.filter(e => !(e.status && e.status.trim().toLowerCase() === 'completed')).slice(0, 3).map((ev, i) => (
            <div key={`${ev.slug || ev.id || 'ev'}-${i}`} className={`card au d${i + 1}`} onClick={() => navigate(`/events/${ev.slug}`)} style={{ cursor: 'pointer', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#fff', border: '1px solid var(--border)' }}>
              <div style={{ aspectRatio: '16/9', position: 'relative', overflow: 'hidden', background: 'var(--surface-container-low)' }}>
                <div style={{ position: 'absolute', inset: 0, background: POSTER_THEMES[i % 6].bg, zIndex: 0 }} />
                {ev.bannerImg && <img src={ev.bannerImg} alt={ev.title} style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'relative', zIndex: 1 }} />}
                <div style={{ position: 'absolute', top: 12, left: 12, zIndex: 2 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#fff', background: 'var(--primary)', padding: '4px 10px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '.1em' }}>{ev.type}</span>
                </div>
              </div>
              <div style={{ padding: 24 }}>
                <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                  <div style={{ background: 'var(--surface-container-low)', borderRadius: 8, padding: '8px 12px', textAlign: 'center', minWidth: 50 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase' }}>{formatDate(ev.month, 'month')}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--on-surface)' }}>{formatDate(ev.day, 'day')}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, color: 'var(--on-surface)', lineHeight: 1.3, marginBottom: 8 }}>{ev.title}</h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, fontSize: 12, color: 'var(--on-surface-variant)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Clock size={13} /> {formatTime(ev.time)}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><MapPin size={13} /> {ev.loc}</div>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--primary)', borderTop: '1px solid var(--slate-100)', paddingTop: 16 }}>
                  Reserve Entry Protocol <ChevronRight size={14} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </Sec>

      {/* CTA SECTION */}
      <Sec py={140} style={{ position: 'relative', overflow: 'hidden', background: 'var(--on-surface)' }}>
        <div className="blob" style={{ position: 'absolute', top: -200, left: -200, width: 600, height: 600, background: 'var(--primary)', opacity: .08, filter: 'blur(120px)' }} />
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 720, margin: '0 auto' }}>
          <div className="eyebrow au" style={{ color: 'var(--secondary)' }}>Ready for Uplink?</div>
          <h2 className="au" style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 6vw, 64px)', fontWeight: 800, color: '#fff', lineHeight: 1, letterSpacing: '-0.04em', marginBottom: 40 }}>Architect the <span className="gtext">Future</span> with Us</h2>
          <p className="au" style={{ fontSize: 19, color: 'rgba(255,255,255,.5)', lineHeight: 1.6, marginBottom: 64, fontWeight: 500 }}>Join an elite community of student engineers and designers. Initialize your enrollment today.</p>
          <div className="au" style={{ display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/membership" className="btn btn-primary btn-lg" style={{ minWidth: 240 }}>
              Initialize Enrollment
            </Link>
            <Link to="/verify" className="btn btn-white btn-lg" style={{ minWidth: 180 }}>
              Verify Certificate
            </Link>
          </div>
        </div>
      </Sec>
    </div>
  );
};

export default HomePage;
